/**
 * Google Drive provider UI — Connect, Create-Folder, Disconnect, Sync, debug.
 */

interface GoogleDriveRowProps {
  isConfigured: boolean;
  isConnected: boolean;
  isEnabled: boolean;
  isWorking: boolean;
  clientId: string;
  folderUrl: string | null;
  savedEmail: string | null;
  needsFolderCreation: boolean;
  statusMessage: string | null;
  onConnect: () => void;
  onCreateFolder: () => void;
  onDisconnect: () => void;
}

export function GoogleDriveRow(props: GoogleDriveRowProps) {
  const statusLabel = !props.isConfigured
    ? 'not configured'
    : props.needsFolderCreation
      ? 'folder needed'
      : props.isConnected
        ? 'connected'
        : props.isEnabled
          ? 'sign-in required'
          : 'off';
  const statusClass = props.isConnected
    ? 'text-emerald-600'
    : props.needsFolderCreation
      ? 'text-amber-600'
      : 'text-stone-500 dark:text-stone-400';

  return (
    <div className="mt-4 rounded-xl border border-stone-200 p-3 dark:border-stone-700">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Google Drive</span>
        <span className={`text-xs ${statusClass}`}>{statusLabel}</span>
      </div>

      {!props.isConfigured && (
        <p className="mb-2 text-xs text-stone-500 dark:text-stone-400">
          Google Drive is not available in this build.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {props.needsFolderCreation && props.isConfigured ? (
          <button
            onClick={props.onCreateFolder}
            disabled={props.isWorking}
            className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
          >
            Create SKJV Storage
          </button>
        ) : !props.isEnabled || !props.isConnected ? (
          <button
            onClick={props.onConnect}
            disabled={props.isWorking || !props.isConfigured}
            className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
          >
            Connect Google Drive
          </button>
        ) : (
          <button
            onClick={props.onDisconnect}
            disabled={props.isWorking}
            className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            Disconnect
          </button>
        )}
      </div>

      {props.statusMessage && (
        <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">{props.statusMessage}</p>
      )}

      <details className="mt-3 text-xs text-stone-500 dark:text-stone-400">
        <summary className="cursor-pointer select-none">Details</summary>
        <div className="mt-2 space-y-1 rounded-lg bg-stone-100 p-2 dark:bg-stone-800">
          <div className="font-medium text-stone-600 dark:text-stone-400">
            VITE_GOOGLE_OAUTH_CLIENT_ID
          </div>
          <code className="block break-all font-mono text-[11px] text-stone-700 dark:text-stone-300">
            {props.clientId || '(unset — add to packages/web/.env.local and restart pnpm dev)'}
          </code>
          <div className="pt-1 text-stone-500 dark:text-stone-400">
            length: {props.clientId.length}
            {props.clientId && !props.clientId.endsWith('.apps.googleusercontent.com') && (
              <span className="ml-2 text-amber-600">
                ⚠ doesn't end in .apps.googleusercontent.com
              </span>
            )}
          </div>
          <div className="pt-2 font-medium text-stone-600 dark:text-stone-400">Signed in as</div>
          {props.savedEmail ? (
            <code className="block break-all font-mono text-[11px] text-stone-700 dark:text-stone-300">
              {props.savedEmail}
            </code>
          ) : (
            <span className="text-stone-500 dark:text-stone-400">(not connected)</span>
          )}
          <div className="pt-2 font-medium text-stone-600 dark:text-stone-400">SKJV folder</div>
          {props.folderUrl ? (
            <a
              href={props.folderUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="block break-all font-mono text-[11px] text-cyan-600 underline hover:text-cyan-500"
            >
              {props.folderUrl}
            </a>
          ) : (
            <span className="text-stone-500 dark:text-stone-400">(not created yet)</span>
          )}
        </div>
      </details>
    </div>
  );
}
